import React, { FC, ReactElement, memo } from 'react';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import List from '@mui/material/List';
import { Theme } from '@mui/material';
// Components
import { v4 as uuidv4 } from 'uuid';
import SelectedAddressCard from './selectedAddressCard';
// Resources
import { Address, AddressType, AutoCompleteAddress, Favorite } from '../../core/apiTypes';
import { getAddressNameAndDescription } from '../../utils/business';
import { asyncStorageApi, KEY_RECENT_SEARCH } from '../../localStorageAPI';

interface SearchAddressListProps {
    addresses: AutoCompleteAddress[];
    onClick: (data: AutoCompleteAddress) => any;
    favoritesToAdd?: Favorite[];
    onClickFavorite?: (data: Address) => any;
    shouldDisplayRecentSearch?: boolean;
}

const SearchAddressList: FC<SearchAddressListProps> = (props): ReactElement => {
    const { addresses, onClick, favoritesToAdd = [], onClickFavorite, shouldDisplayRecentSearch = false } = props;
    const classes = useStyles();

    const renderAddresses = (): JSX.Element[] => {
        if (addresses?.length) {
            const res = addresses.map((address: AutoCompleteAddress): JSX.Element => {
                const { details } = address;
                if (!details) {
                    return null;
                }

                const { name, description } = getAddressNameAndDescription(details);

                return (
                    <SelectedAddressCard
                        key={address.place_id}
                        title={name}
                        description={description}
                        onClick={onClick(address)}
                        type={AddressType.ADDRESS}
                    />
                );
            });
            if (favoritesToAdd.length > 0) {
                favoritesToAdd.forEach((fav) => {
                    res.unshift(
                        <SelectedAddressCard
                            key={fav.uuid || uuidv4()}
                            title={fav.name}
                            description={fav.details.full_name}
                            onClick={onClickFavorite(fav)}
                            type={fav.type === AddressType.FAVORITE ? fav.type : AddressType.RECENT}
                        />,
                    );
                });
            }
            return res;
        }
        return null;
    };

    const renderRecentSearch = (): JSX.Element[] => {
        const recentSearchArray = asyncStorageApi.restoreData(KEY_RECENT_SEARCH);
        return recentSearchArray
            ?.filter((el) => el.address)
            ?.map((el) => (
                <SelectedAddressCard
                    key={uuidv4()}
                    title={el.address.details.name}
                    description={el.address.details.full_name}
                    onClick={onClickFavorite(el.address)}
                    type={AddressType.RECENT}
                />
            ));
    };

    if (
        (shouldDisplayRecentSearch && asyncStorageApi.restoreData(KEY_RECENT_SEARCH)?.length < 1) ||
        (!shouldDisplayRecentSearch && addresses?.length + favoritesToAdd?.length < 1)
    ) {
        return null;
    }

    return (
        <>
            <List className={classes.margin} id="addressList">
                {shouldDisplayRecentSearch ? renderRecentSearch() : renderAddresses()}
            </List>
        </>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        margin: {
            paddingLeft: 0,
            marginLeft: 0,
            '& .MuiButtonBase-root': {
                borderBottom: `1px solid ${theme.palette.colors.background.separator}`,
            },
        },
    }),
);

export default memo(SearchAddressList);
